import { useQuery } from "@tanstack/react-query";
import { ProductsService } from "@/services/products.service";
import { useTranslation } from "react-i18next";

type ProductType = 'products' | 'coproducers' | 'affiliates' | 'referral_links';

interface UseProductsDataParams {
  type: ProductType;
  currentPage: number;
  status?: string;
  query: string;
  enabled?: boolean;
}

export const useProductsData = ({
  type,
  currentPage,
  status,
  query,
  enabled = true
}: UseProductsDataParams) => {
  const { t } = useTranslation('common');

  const getQueryKey = () => {
    const baseKey = [type, currentPage, query];
    return type === 'products' ? [...baseKey, status] : baseKey;
  };

  const getQueryFn = () => {
    const params = { currentPage, query };

    switch (type) {
      case 'products':
        return () => ProductsService.getProducts({ ...params, status });
      case 'coproducers':
        return () => ProductsService.getCoproducers(params);
      case 'affiliates':
        return () => ProductsService.getMyAffiliates(params);
      case 'referral_links':
        return () => Promise.resolve({ data: [], meta: null, links: null });
      default:
        throw new Error(`Invalid product type: ${type}`);
    }
  };

  const {
    data: products,
    isPending: isLoading,
    refetch,
    isRefetching,
    error,
  } = useQuery({
    queryKey: getQueryKey(),
    queryFn: getQueryFn(),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos
  });

  return {
    products,
    isLoading,
    refetch,
    isRefetching,
    error: error
      ? (error as Error).message || t('data_listing_error')
      : null,
  };
};
